import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';
import { Button, ButtonSizes } from 'atoms';

/** Interaction state as it arrives on the wire: 'saved' | 'applied' | null. */
export type CareerVacancyInteraction = 'saved' | 'applied' | null;

export interface ICareerVacancyCard {
  title: string;
  companyName: string;
  location: string;
  /** Raw wire enum values — the card resolves labels via i18n and hides blanks. */
  workMode: string;
  seniority: string;
  employmentType: string;
  /** Pre-formatted salary range; empty string hides the chip. */
  salary: string;
  description: string;
  directionTitles: string[];
  skillNames: string[];
  applyUrl: string;
  interaction: CareerVacancyInteraction;
  onSave: () => void;
  onApply: () => void;
  actionLoading: boolean;
}

const MAX_SKILL_CHIPS = 8;

/**
 * One vacancy in the feed (spec 0018 §B2).
 *
 * The Save / Apply state is driven purely by `interaction` (server truth):
 * `applied` outranks `saved`, and the actions are one-way — once a state is
 * reached its button becomes a non-clickable "done" marker rather than a toggle
 * (there is no un-save / un-apply on the backend). "Открыть" always links out
 * to the external application URL in a new tab.
 */
export const CareerVacancyCard = memo(
  ({
    title,
    companyName,
    location,
    workMode,
    seniority,
    employmentType,
    salary,
    description,
    directionTitles,
    skillNames,
    applyUrl,
    interaction,
    onSave,
    onApply,
    actionLoading,
  }: ICareerVacancyCard) => {
    const { t } = useTranslation();

    const isApplied = interaction === 'applied';
    const isSaved = interaction === 'saved';

    const workModeLabel = workMode
      ? t(`careerProfile.workMode.${workMode}`, { defaultValue: workMode })
      : '';
    const seniorityLabel = seniority
      ? t(`careerProfile.seniority.${seniority}`, { defaultValue: seniority })
      : '';
    const employmentLabel = employmentType
      ? t(`careerCenter.vacancies.employment.${employmentType}`, { defaultValue: employmentType })
      : '';

    const skillChips = skillNames.slice(0, MAX_SKILL_CHIPS);
    const extraSkills = skillNames.length - skillChips.length;

    return (
      <Card>
        <CardHeader>
          <HeaderMain>
            <CardTitle>{title}</CardTitle>
            <CompanyLine>
              <Company>{companyName}</Company>
              {location ? <Location>· {location}</Location> : null}
            </CompanyLine>
          </HeaderMain>

          <OpenLink href={applyUrl} target="_blank" rel="noopener noreferrer">
            {t('careerCenter.vacancies.open')}
          </OpenLink>
        </CardHeader>

        <ChipRow>
          {workModeLabel ? <Chip>{workModeLabel}</Chip> : null}
          {seniorityLabel ? <Chip>{seniorityLabel}</Chip> : null}
          {employmentLabel ? <Chip>{employmentLabel}</Chip> : null}
          {salary ? <Chip accent>{salary}</Chip> : null}
        </ChipRow>

        {description ? <Description>{description}</Description> : null}

        {directionTitles.length || skillChips.length ? (
          <TagRow>
            {directionTitles.map((direction) => (
              <DirectionTag key={`dir-${direction}`}>{direction}</DirectionTag>
            ))}
            {skillChips.map((skill) => (
              <SkillTag key={`skill-${skill}`}>{skill}</SkillTag>
            ))}
            {extraSkills > 0 ? (
              <SkillTag>{t('careerCenter.vacancies.moreSkills', { n: extraSkills })}</SkillTag>
            ) : null}
          </TagRow>
        ) : null}

        <Actions>
          <ActionButton
            variant={isSaved || isApplied ? 'outline' : 'text'}
            size={ButtonSizes.sm}
            disabled={isSaved || isApplied || actionLoading}
            onClick={onSave}>
            {isSaved || isApplied
              ? t('careerCenter.vacancies.saved')
              : t('careerCenter.vacancies.save')}
          </ActionButton>

          <ApplyButton
            applied={isApplied}
            size={ButtonSizes.sm}
            disabled={isApplied || actionLoading}
            isLoading={actionLoading && !isApplied}
            onClick={onApply}>
            {isApplied ? t('careerCenter.vacancies.applied') : t('careerCenter.vacancies.apply')}
          </ApplyButton>
        </Actions>
      </Card>
    );
  },
);

const Card = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  padding: 20px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  background-color: ${Colors.white};

  ${tablet} {
    padding: 24px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderMain = styled.div`
  min-width: 0;
`;

const CardTitle = styled.h3`
  margin: 0 0 4px 0;
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
`;

const CompanyLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
`;

const Company = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Location = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const OpenLink = styled.a`
  flex-shrink: 0;
  padding: 8px 14px;
  border: 1px solid ${Colors.main};
  border-radius: 10px;
  color: ${Colors.main};
  white-space: nowrap;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    background-color: ${Colors.main};
    color: ${Colors.white};
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const Chip = styled.span<{ accent?: boolean }>`
  padding: 4px 10px;
  border-radius: 8px;
  background-color: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}

  ${({ accent }) =>
    accent &&
    css`
      background-color: ${Colors.marker_green};
      color: ${Colors.black};
    `}
`;

const Description = styled.p`
  margin: 16px 0 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const DirectionTag = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  background-color: ${Colors.main_light};
  color: ${Colors.main_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const SkillTag = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid ${Colors.system_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
`;

const ActionButton = styled(Button)`
  width: auto;
`;

const ApplyButton = styled(Button)<{ applied: boolean }>`
  width: auto;

  ${({ applied }) =>
    applied &&
    css`
      opacity: 0.9;
    `}
`;
